import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, API, money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Folder, FolderOpen, FileDown, Search, FileText, Receipt, Truck, ArrowLeft, Pencil, Trash2, Download, Loader2 } from "lucide-react";

const FOLDERS = [
  { key: "quotations", label: "Quotations", icon: FileText, api: "/quotations", pdf: (d) => `/quotations/${d.id}/pdf`, ref: (d) => d.invoice_number ? `→ ${d.invoice_number}` : "", edit: (d) => `/quotations/${d.id}`, del: (d) => `/quotations/${d.id}` },
  { key: "invoices", label: "Invoices", icon: Receipt, api: "/invoices", pdf: (d) => `/invoices/${d.id}/pdf`, ref: (d) => d.quotation_number ? `from ${d.quotation_number}` : "", edit: (d) => `/invoices/${d.id}/edit`, del: (d) => `/invoices/${d.id}` },
  { key: "delivery-notes", label: "Delivery Notes", icon: Truck, api: "/delivery-notes", pdf: (d) => `/delivery-notes/${d.id}/pdf`, ref: (d) => d.ref_number ? `for ${d.ref_number}` : "", edit: (d) => `/delivery-notes?edit=${d.id}`, del: (d) => `/delivery-notes/${d.id}` },
];

const monthKey = (d) => (d.date || d.delivery_date || d.created_at || "").slice(0, 7);
const monthLabel = (k) => k ? new Date(`${k}-01T00:00:00`).toLocaleDateString("en-ZA", { month: "long", year: "numeric" }) : "Undated";

export default function DocumentFolders() {
  const [sp, setSp] = useSearchParams();
  const active = FOLDERS.find((f) => f.key === sp.get("folder"));
  const [counts, setCounts] = useState({});
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [zipping, setZipping] = useState(null);
  const downloadZip = async (k) => {
    setZipping(k);
    try {
      const res = await api.get(`/folders/${active.key}/zip`, { params: { month: k }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${active.key}-${k}.zip`; a.click(); URL.revokeObjectURL(url);
      toast.success(`${monthLabel(k)} ${active.label.toLowerCase()} downloaded`);
    } catch { toast.error("Could not build the ZIP"); }
    finally { setZipping(null); }
  };
  const navigate = useNavigate();
  const { can } = useAuth();
  const editable = can("quotations");
  const loadDocs = () => { if (!active) return; setLoading(true); api.get(active.api).then(({ data }) => setDocs(data)).catch(() => setDocs([])).finally(() => setLoading(false)); };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.number}? This cannot be undone.`)) return;
    try { await api.delete(active.del(d)); toast.success(`${d.number} deleted`); loadDocs(); setCounts((c) => ({ ...c, [active.key]: Math.max(0, (c[active.key] || 1) - 1) })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    FOLDERS.forEach((f) => api.get(f.api).then(({ data }) => setCounts((c) => ({ ...c, [f.key]: data.length }))).catch(() => {}));
  }, []);
  useEffect(() => { setQ(""); loadDocs(); }, [active?.key]);

  const grouped = useMemo(() => {
    const filtered = docs.filter((d) => !q || `${d.number} ${d.customer?.company_name || ""} ${d.customer?.contact_person || ""} ${d.quotation_number || ""} ${d.invoice_number || ""} ${d.ref_number || ""}`.toLowerCase().includes(q.toLowerCase()));
    const map = {};
    filtered.forEach((d) => { const k = monthKey(d); (map[k] ||= []).push(d); });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [docs, q]);

  if (!active) {
    return (
      <div>
        <PageHeader title="Document Folders" subtitle="Every quotation, invoice and delivery note is filed here automatically the moment it is created." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="folder-grid">
          {FOLDERS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.key} onClick={() => setSp({ folder: f.key })} className="group text-left bg-white border border-border rounded-md p-5 hover:border-primary transition-colors" data-testid={`folder-${f.key}`}>
                <div className="flex items-start justify-between">
                  <Folder className="h-10 w-10 text-primary/80 group-hover:hidden" /><FolderOpen className="h-10 w-10 text-primary hidden group-hover:block" />
                  <Badge variant="outline" className="tabular" data-testid={`folder-count-${f.key}`}>{counts[f.key] ?? "…"}</Badge>
                </div>
                <div className="mt-4 font-display font-semibold text-lg flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /> {f.label}</div>
                <div className="text-xs text-muted-foreground mt-1">Filed by month · open or download any PDF</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={`${active.label} folder`} subtitle={`${docs.length} document${docs.length === 1 ? "" : "s"} filed`}>
        <Button variant="outline" onClick={() => setSp({})} data-testid="folder-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> All folders</Button>
      </PageHeader>
      <div className="relative max-w-sm mb-4"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number or client…" className="pl-9" data-testid="folder-search" /></div>
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && grouped.length === 0 && <div className="bg-white border border-border rounded-md py-14 text-center text-muted-foreground" data-testid="folder-empty">Nothing filed here yet.</div>}
      {grouped.map(([k, list]) => (
        <div key={k} className="mb-6" data-testid={`folder-month-${k || "undated"}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground"><Folder className="h-3.5 w-3.5" /> {monthLabel(k)} <span className="tabular">({list.length})</span></div>
            {k && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadZip(k)} disabled={zipping === k} data-testid={`folder-zip-${k}`}>{zipping === k ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />} Download month as ZIP</Button>}
          </div>
          <div className="bg-white border border-border rounded-md">
            <Table>
              <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Number</TableHead><TableHead>Client</TableHead><TableHead>Linked</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {list.map((d) => (
                  <TableRow key={d.id} data-testid={`folder-doc-${d.number}`}>
                    <TableCell className="font-mono font-medium">{d.number}</TableCell>
                    <TableCell>{d.customer?.company_name || d.customer?.contact_person || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{active.ref(d) || "—"}</TableCell>
                    <TableCell className="text-sm">{fmtDate(d.date || d.delivery_date || d.created_at)}</TableCell>
                    <TableCell className="text-right tabular">{d.total != null ? money(d.total) : "—"}</TableCell>
                    <TableCell><Badge variant="outline">{d.status || "—"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <a href={`${API}${active.pdf(d)}`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8" title="Open PDF" data-testid={`folder-pdf-${d.number}`}><FileDown className="h-4 w-4" /></Button></a>
                        {editable && active.edit && <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => navigate(active.edit(d))} data-testid={`folder-edit-${d.number}`}><Pencil className="h-4 w-4" /></Button>}
                        {editable && active.del && (!active.canDel || active.canDel(d)) && <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Delete" onClick={() => remove(d)} data-testid={`folder-delete-${d.number}`}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
